import type { HttpError } from '@/queries/auth-error'
import type { EntityInvalidation } from '@/queries/entity-keys'

/**
 * Declares which entities a mutation invalidates on success.
 *
 * - `awaited`: keys whose `invalidateQueries` promise is awaited before the
 *   mutation's `onSuccess` resolves. Use for entities the next screen will
 *   read immediately.
 * - `background`: fired-and-forgotten. Use for entities you want fresh
 *   eventually but don't need to block on.
 *
 * Pass `'skip'` for mutations whose result genuinely does not affect the cache
 * (e.g. previews, validations).
 */
export type InvalidationConfig = {
  awaited?: EntityInvalidation[]
  background?: EntityInvalidation[]
}

declare module '@tanstack/vue-query' {
  interface Register {
    /**
     * Every `queryFn` / `mutationFn` in `src/queries/*` throws `HttpError`
     * for non-2xx responses, so vue-query callbacks (`onError`, `error`
     * refs, etc.) receive `HttpError` typed errors throughout the app.
     */
    defaultError: HttpError
    mutationMeta: {
      invalidation?: InvalidationConfig
    }
  }
}

type MutationOptionsInput<TData, TError, TVariables, TContext> = {
  mutationFn: (variables: TVariables) => Promise<TData>
  invalidation: InvalidationConfig | 'skip'
  onMutate?: (variables: TVariables) => TContext | Promise<TContext>
  onSuccess?: (data: TData, variables: TVariables, context: TContext) => unknown
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => unknown
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    context: TContext | undefined,
  ) => unknown
  retry?: number | boolean | ((failureCount: number, error: TError) => boolean)
  retryDelay?: number | ((failureCount: number, error: TError) => number)
  mutationKey?: readonly unknown[]
}

type MutationOptionsOutput<TData, TError, TVariables, TContext> = Omit<
  MutationOptionsInput<TData, TError, TVariables, TContext>,
  'invalidation'
> & {
  meta?: { invalidation: InvalidationConfig }
}

/**
 * Wraps `useMutation` options so that `invalidation` is moved onto
 * `meta.invalidation`, where the global `MutationCache.onSuccess` handler can
 * pick it up. Components never invalidate manually.
 */
export function mutationOptions<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  options: MutationOptionsInput<TData, TError, TVariables, TContext>,
): MutationOptionsOutput<TData, TError, TVariables, TContext> {
  const { invalidation, ...rest } = options
  return {
    ...rest,
    ...(invalidation !== 'skip' && { meta: { invalidation } }),
  }
}
